import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './SuperSearch.module.css';
import useDebounce from './useDebounce';
import InputVariant1 from './InputVariants/InputVariant1';
import searchIcon from "./searchIcon.svg"
import closeIcon from "./closeIcon.svg"
import api from '../../http';
import { ErrorHandlingMessage } from 'Utils/ErrorHandlingMessage';
import CustomLabel from 'Common/CustomLabel/CustomLabel';
import InputVariant2 from './InputVariants/InputVariant2/InputVariant2';
import { toast } from 'react-toastify';
import { NO_DATA_FOUND } from 'Common/ToastConstants';
import { Tooltip } from 'react-tooltip';
import { LuX } from 'react-icons/lu';

// =============================== CUSTOM HELPER FUNCTIONS ==========================
const getupdateArray = (cols, rows) => {
  const updatedarr = rows.map((row) => {
    let obj = {};
    cols.forEach((ele) => {
      // const ddd = ele.replace(/\s/g, '').toLowerCase()
      obj[ele.bindingValue] = row[ele.bindingValue];
    });
    return obj;
  });
  return updatedarr;
};

function getValue(obj, path) {
  if (!path) return obj;
  const properties = path.split('.');
  return getValue(obj[properties.shift()], properties.join('.'));
}

// Function to truncate text based on column width - wider columns show more words
const truncateText = (text, columnWidth = 'auto') => {
  if (!text) return '';

  // Parse column width to determine how many words to show
  let maxWords = 8; // default

  if (columnWidth && typeof columnWidth === 'string') {
    if (columnWidth.includes('px')) {
      const width = parseInt(columnWidth);
      if (width >= 600) maxWords = 15;      // Very wide columns
      else if (width >= 400) maxWords = 12;  // Wide columns
      else if (width >= 300) maxWords = 10;  // Medium columns
      else if (width >= 200) maxWords = 8;   // Standard columns
      else if (width >= 150) maxWords = 6;   // Narrow columns
      else maxWords = 4;                      // Very narrow columns
    }
  }

  const words = text.split(' ');
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(' ') + '...';
};

const getScrollParent = (element) => {
  if (!element) return null;
  let parent = element.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const overflow = style.overflow;
    if (/(auto|scroll)/.test(overflowY) || /(auto|scroll)/.test(overflow)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

const DROPDOWN_PORTAL_Z_INDEX = 1050;
// Tooltips must render above the dropdown table when shown inside it
const TOOLTIP_Z_INDEX = 1060;

const SuperSearch = (props) => {
  const [tableData, settableData] = useState([]);
  const [selectedRowNumber, setselectedRowNumber] = useState(0);
  const [showTable, setshowTable] = useState(false);
  const [searchStatus, setsearchStatus] = useState(false);
  const [s_Value, sets_Value] = useState('');
  const [loading, setLoading] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const resetComponentRef = useRef(() => {});

  const debouncedSearchTerm = useDebounce(props.searchValue, 800);

  // Measure wrapper and position dropdown; update on scroll/resize so it stays aligned (e.g. inside modals)
  useLayoutEffect(() => {
    if (!showTable || !wrapperRef.current) return;
    const update = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();

        const dropdownHeight = props.tableHeight || 300;
        const spaceBelow = window.innerHeight - rect.bottom;

        if (spaceBelow < dropdownHeight) {
          
          setDropdownRect({
            top: rect.top - dropdownHeight,
            left: rect.left,
            width: rect.width,
          });
        } else {
          setDropdownRect({
            top: rect.bottom,
            left: rect.left,
            width: rect.width,
          });
        }
      }
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showTable]);
  // ========================= ON KEY DOWN PRESSED INPUT FIELD======================
  const onKeyDownPressed = async (e) => {
    if (e.keyCode === 13) {
      // User pressed the ENTER key
      if (tableData.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        await props.getSelectedData(tableData[selectedRowNumber], e.keyCode);
        setshowTable(false);
        //setselectedRowNumber(0)
        setsearchStatus(false);
        sets_Value('');
      }
    } else if (e.keyCode === 38) {
      // if user presses the UP
      if (selectedRowNumber > 0) {
        setselectedRowNumber((prevSelectedRow) => prevSelectedRow - 1);
        const selectedRowUP = selectedRowNumber - 1
        var elem = document.getElementById(`${props.identifier}_${selectedRowUP}`);
        elem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    } else if (e.keyCode === 40) {
      // IF USER PRESSES DOWN KEY
      if (selectedRowNumber < tableData.length - 1) {
        setselectedRowNumber((prevSelectedRow) => prevSelectedRow + 1);
        const selectedRowDown = selectedRowNumber + 1
        var elem = document.getElementById(`${props.identifier}_${selectedRowDown}`);
        elem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    } else if (e.keyCode === 27) {
      // IF USER PRESSES ESC KEY THEN RESET THE COMPONENT TO ITS ORIGINAL STATE
      props.getSelectedData(null);
      settableData([]);
      setshowTable(false);
      setselectedRowNumber(0);
    } else if (e.keyCode === 9) {
      // IF USER PRESSES THE TAB BUTTON
      if (tableData.length > 0) {
        props.getSelectedData(tableData[selectedRowNumber]);
        setshowTable(false);
      }
    }
    else if (e.keyCode === 8) {
      // IF USER PRESSES THE BACKSPACE BUTTON
    }
  };

  // ====================== ON CLICKING SINGLE ROW OF TABLE ================
  const handleSingleRow = (singlerow, index) => {
    setselectedRowNumber(index);
    props.getSelectedData(singlerow);
    setshowTable(false);
    setsearchStatus(false);
    sets_Value('');
    setselectedRowNumber(0);
    settableData([])
  };

  // =========================== ON CHANGE HANDLER OF THE INPUT FIELD =============================
  const onHandleChange = async (e) => {
    let eventValue = e.target.value;
    eventValue = eventValue.trimStart()
    props.onChange(eventValue);
    sets_Value(eventValue);
    if (eventValue === '') {
      props.getSelectedData(null);
      setsearchStatus(false);
      setselectedRowNumber(0);
    } else {
      setsearchStatus(true);
    }
  };

  // ====================== RESETING THE COMPONENT TO ITS ORIGINAL STATE================
  const resetComponent = () => {
    props.getSelectedData(null);
    settableData([]);
    setselectedRowNumber(0);
    setshowTable(false);
    setsearchStatus(false);
    sets_Value('');
  };
  resetComponentRef.current = resetComponent;

  useEffect(() => {
    if (searchStatus) {
      const sanitizedTerm = encodeURIComponent(debouncedSearchTerm);
      if (sanitizedTerm.length >= 3) {
        (async () => {
          try {
            setLoading(true);
            let response = null;

            if (props.customData?.length > 0) {
              response = props.customData.filter((cdata) => cdata.label.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));

              settableData(response);
              setshowTable(response.length > 0);
              if (response.length === 0) {
                toast.error(NO_DATA_FOUND);
              }
              setLoading(false);
            } else {
              if (props.method === 'post') {
                if (!props.postBody) {
                  throw 'Post Body prop is not defined';
                } else {
                  response = await api.post(props.ApiLink, props.postBody);
                }
              } else if (props.method === 'get') {
                response = await api.get(`${props.ApiLink}${sanitizedTerm}`);
              } else {
                throw 'Method not allowed';
              }

              settableData(response.data.dataModel.data);
              setshowTable(response.data.dataModel.data.length > 0);
              if (response.data.dataModel.data.length === 0) {
                toast.error(NO_DATA_FOUND);
              }
            }

            setLoading(false);
          } catch (error) {
            ErrorHandlingMessage(error);
          }
        })();
      }
    }
  }, [props.ApiLink, props.method, props.customData, props.responseStructure, debouncedSearchTerm]);



  const inputVariants = {
    variant1: (
      <InputVariant1
        variantSearchIcon={props.searchIcon}
        variantCrossIcon={props.crossIcon}
        variantThemeColor={props.themeColor}
        searchValue={props.searchValue}
        variantPlaceholder={props.placeholder}
        variantDisabled={props.disabled}
        variantID={props.id}
        handleChange={onHandleChange}
        handleOnKeyDown={onKeyDownPressed}
        resetComponent={resetComponent}
        loading={loading}
        variantInputHeight={props.identifier === "chargesTable" ? props.inputHeight || 30 : undefined}
        />
    ),
    variant2: <InputVariant2
      variantSearchIcon={props.searchIcon}
      variantCrossIcon={props.crossIcon}
      variantThemeColor={props.themeColor}
      searchValue={props.searchValue}
      variantPlaceholder={props.placeholder}
      variantDisabled={props.disabled}
      variantID={props.id}
      handleChange={onHandleChange}
      handleOnKeyDown={onKeyDownPressed}
      resetComponent={resetComponent}
      loading={loading}

    />,
  };

  const showDropdown = showTable && searchStatus && s_Value === props.searchValue;

  // Close dropdown when clicking outside the input or the list (same as close button)
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e) => {
      const insideWrapper = wrapperRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (insideWrapper || insideDropdown) return;
      resetComponentRef.current();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Disable page + scrollable parent scroll while dropdown is open, restore on close/unmount
  useEffect(() => {
    const body = document.body;
    const originalBodyOverflow = body.style.overflow;
    let scrollParent = null;
    let originalParentOverflow;

    if (showDropdown) {
      body.style.overflow = 'hidden';

      if (wrapperRef.current && typeof window !== 'undefined') {
        scrollParent = getScrollParent(wrapperRef.current);
        if (scrollParent) {
          originalParentOverflow = scrollParent.style.overflow;
          scrollParent.style.overflow = 'hidden';
        }
      }
    }

    return () => {
      body.style.overflow = originalBodyOverflow;
      if (scrollParent) {
        scrollParent.style.overflow = originalParentOverflow;
      }
    };
  }, [showDropdown]);

  const dropdownContent = (
    <>
      <div
        ref={dropdownRef}
        className={`${styles.tableWrapper} tablepadding5`}
        style={{
          position: 'fixed',
          top: dropdownRect?.top ?? 0,
          left: dropdownRect?.left ?? 0,
          width: props.tableWidth || dropdownRect?.width || '100%',
          maxHeight: props.tableHeight ? `${props.tableHeight}px` : undefined,
          overflow: 'auto',
          zIndex: DROPDOWN_PORTAL_Z_INDEX,
          background: '#faf8f8',
          boxShadow: '0 7px 10px rgb(0 0 0 / 40%)',
        }}
      >
        <table className={styles.table} style={{ height: 'inherit' }}>
          {props.allowHeaders && (
            <thead className={styles.table_head}>
              <tr style={{ display: 'flex' }}>
                {props.tableData.map((data, i) => (
                  <th className={data.columnWidth ? '' : styles.flexGrow} style={{ width: data.columnWidth ? data.columnWidth : 'auto' }} key={i}>
                    {' '}{data.columName}{' '}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className={styles.table_body}>
            {props.customData?.length > 0
              ? getupdateArray(props.tableData, tableData).map((trows, index) => {
                  const selected = index === selectedRowNumber;
                  return (
                    <tr
                      id={`${props.identifier}_${index}`}
                      key={index}
                      className={selected ? styles.selected_row : ''}
                      onClick={() => handleSingleRow(tableData[index], index)}
                    >
                      {props.tableData.map((tcols, i) => (
                        i === 0 ? (
                          <td
                            key={i}
                            className={tcols.columnWidth ? '' : styles.flexGrow}
                            style={{
                              width: tcols.columnWidth ? tcols.columnWidth : '50%',
                              fontSize: '12px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            {...(tcols.showTooltip && {
                              'data-tooltip-id': `${props.identifier}_${index}_${i}_tooltip`,
                              'data-tooltip-content': trows[tcols.bindingValue].toString(),
                            })}
                          >
                            <span className="truncate text-gray-900 hover:text-primary transition-colors">
                              {truncateText(trows[tcols.bindingValue].toString(), tcols.columnWidth)}
                            </span>
                          </td>
                        ) : (
                          <td key={i} className={tcols.columnWidth ? '' : styles.flexGrow} style={{ width: tcols.columnWidth ? tcols.columnWidth : '50%' }}>
                            <span className="text-gray-900 hover:text-primary transition-colors">{trows[tcols.bindingValue]}</span>
                          </td>
                        )
                      ))}
                    </tr>
                  );
                })
              : getupdateArray(props.tableData, tableData).map((trows, index) => {
                  const selected = index === selectedRowNumber;
                  return (
                    <tr
                      id={`${props.identifier}_${index}`}
                      key={index}
                      className={selected ? styles.selected_row : ''}
                      onClick={() => handleSingleRow(tableData[index], index)}
                    >
                      {props.tableData.map((tcols, i) => (
                        i === 0 ? (
                          <td
                            key={i}
                            className={tcols.columnWidth ? '' : styles.flexGrow}
                            style={{
                              width: tcols.columnWidth ? tcols.columnWidth : '50%',
                              fontSize: '12px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            {...(tcols.showTooltip && {
                              'data-tooltip-id': `${props.identifier}_${index}_${i}_tooltip`,
                              'data-tooltip-content': trows[tcols.bindingValue].toString(),
                            })}
                          >
                            <span className="truncate text-gray-900 hover:text-primary transition-colors">
                              {truncateText(trows[tcols.bindingValue].toString(), tcols.columnWidth)}
                            </span>
                          </td>
                        ) : (
                          <td key={i} className={tcols.columnWidth ? '' : styles.flexGrow} style={{ width: tcols.columnWidth ? tcols.columnWidth : '50%' }}>
                            <span className="text-gray-900 hover:text-primary transition-colors">{trows[tcols.bindingValue]}</span>
                          </td>
                        )
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      {tableData.map((trows, index) => {
        const firstColumn = props.tableData[0];
        if (firstColumn && firstColumn.showTooltip) {
          return (
            <Tooltip
              key={`${props.identifier}_${index}_0_tooltip`}
              id={`${props.identifier}_${index}_0_tooltip`}
              style={{
                zIndex: TOOLTIP_Z_INDEX,
                maxWidth: 400,
                backgroundColor: '#333',
                color: 'white',
              }}
              place="top"
            />
          );
        }
        return null;
      })}
    </>
  );

  return (
    <div ref={wrapperRef} className={` ${props.identifier === "chargesTable" ? "" : styles.mainWrapper}`} style={{ ...props.extraStyling }}>
      {props.label && (<div className='mb-2'> <CustomLabel labelClass={props.labelClass} label={props.label} toolTip={props.toolTip} required={props.labelRequired} /></div>)}

      {inputVariants[props.variantType]}

      {showDropdown && dropdownRect && createPortal(dropdownContent, document.body)}
    </div>
  );
};


// Specifies the default values for props:
SuperSearch.defaultProps = {
  searchIcon: <img src={searchIcon} alt="searchIcon" />,
  crossIcon: <LuX className="w-5 h-5 text-gray-400" />,
  placeholder: "Search ...",
  disabled: false

}

export default SuperSearch;
